import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import TextareaAutosize from '@mui/base/TextareaAutosize';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import BoltIcon from '@mui/icons-material/Bolt';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';

import DiMarkdown from './DiMarkdown';
import AiSocialShareModal from './AiSocialShareModal';
import SaveInsightsModal from './SaveInsightsModal';
import AssistantChatExamplesDialog from './AssistantChatExamplesDialog';
import { ASSISTANT_CHAT_CSS } from './assistantChatStyles';
import type { AssistantChatConfig, AsyncSend, SyncSend } from './assistantChatConfigs';

/**
 * Inject the AssistantChat stylesheet into <head>. The deployed CSS bundle
 * (`app/assets/stylesheets/style.css`) is pre-built and not regenerated from
 * SCSS during normal development, so we ship the styles inside the JS bundle.
 *
 * The single <style id="assistant-chat-styles"> element is reused across
 * mounts (idempotent for multiple <AssistantChat /> instances on a page) but
 * its textContent is re-synced on every call so HMR / hot-reload picks up
 * style updates without a full page refresh.
 */
function ensureStylesInjected(): void {
  if (typeof document === 'undefined') return;
  const id = 'assistant-chat-styles';
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  if (el.textContent !== ASSISTANT_CHAT_CSS) {
    el.textContent = ASSISTANT_CHAT_CSS;
  }
}

/* ==========================================================================
   Types
   ========================================================================== */

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
  pair_id?: any;
  title?: string;
  public_url?: string;
  unique_id?: string;
};

type QueuedMessage = {
  id: number;
  content: string;
};

type QueuePauseReason = 'retryable' | 'blocked' | null;

type ChatEvent = {
  event_unique_id: string;
  event_type: string;
  content: string;
  created_at: string;
  occurred_at?: string;
  received_at_ms?: number;
};

type Thread = {
  id: any;
  agent: string;
  date: string;
  header: string;
  url: string;
  message_type?: string;
};

const DEFAULT_POLL_INTERVALS = [1000, 3000, 7000, 15000, 30000, 60000];
const DEFAULT_MAX_POLL_DURATION = 180000;
const DEFAULT_EVENTS_POLL_INTERVAL = 2000;
const PLACEHOLDER_ROTATION_MS = 3500;
const THREADS_VISIBLE_BATCH = 3;
const THREAD_SEARCH_STORAGE_KEY = 'ac:thread_search';

/* ==========================================================================
   Helpers
   ========================================================================== */

function monogram(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase();
  return (parts[0]!.slice(0, 1) + parts[1]!.slice(0, 1)).toUpperCase();
}

function chipColorFor(agentName: string): string {
  switch (agentName) {
    case 'Lima':     return '#34c759';
    case 'Saturday': return '#1e88e5';
    case 'June':     return '#0ea5e9';
    case 'Summer':   return '#f59e0b';
    default:         return '#6c5ce7';
  }
}

function eventIcon(eventType: string): React.ReactNode {
  switch (eventType) {
    case 'tool_call':
      return <BoltIcon sx={{ color: '#f59e0b' }} />;
    case 'reasoning':
      return <LightbulbOutlinedIcon sx={{ color: '#7c3aed' }} />;
    default:
      return <PendingActionsIcon sx={{ color: '#1e88e5' }} />;
  }
}

function occurredAtMs(event: ChatEvent): number | null {
  if (!event.occurred_at) return null;
  const timestamp = Date.parse(event.occurred_at);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function mergeChatEvents(previous: ChatEvent[], incoming: ChatEvent[]): ChatEvent[] {
  const receivedAt = Date.now();
  const previousById = new Map(previous.map((event) => [event.event_unique_id, event]));
  const incomingIds = new Set(incoming.map((event) => event.event_unique_id));
  const serverOrdered = incoming.map((event) => ({
    ...event,
    received_at_ms:
      previousById.get(event.event_unique_id)?.received_at_ms || receivedAt,
  }));
  const retained = previous.filter((event) => !incomingIds.has(event.event_unique_id));
  const merged = [...retained, ...serverOrdered];
  const eventsWithTimes = merged.map((event, fallbackIndex) => ({
    event,
    fallbackIndex,
    timestamp: occurredAtMs(event),
  }));
  if (eventsWithTimes.some(({ timestamp }) => timestamp == null)) return merged;

  return eventsWithTimes
    .sort((left, right) => {
      if (left.timestamp !== right.timestamp) {
        return left.timestamp! - right.timestamp!;
      }
      return left.fallbackIndex - right.fallbackIndex;
    })
    .map(({ event }) => event);
}

/** Custom hook: rotates ghost placeholder through a list while the field is
 *  empty + unfocused. Pauses if `paused` is true. */
function useRotatingPlaceholder(
  prompts: string[],
  paused: boolean,
  fallback: string
): string {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (paused || prompts.length <= 1) return undefined;
    const id = window.setInterval(
      () => setIdx((i) => (i + 1) % prompts.length),
      PLACEHOLDER_ROTATION_MS
    );
    return () => window.clearInterval(id);
  }, [paused, prompts.length]);

  if (prompts.length === 0) return fallback;
  return `Try: ${prompts[idx % prompts.length]}`;
}

/* ==========================================================================
   Memoized sub-components
   ========================================================================== */

type MessageRowProps = {
  message: ChatMessage;
  prevRole: ChatMessage['role'] | null;
  assistantName: string;
  assistantAvatar: string;
  assistantAccent?: string;
  onCopy: (content: string) => void;
  onDownload: (content: string) => void;
  onDelete: (pairId: any) => void;
};

const MessageRow = React.memo(function MessageRow({
  message,
  prevRole,
  assistantName,
  assistantAvatar,
  assistantAccent,
  onCopy,
  onDownload,
  onDelete,
}: MessageRowProps) {
  if (message.role === 'user') {
    return (
      <div className="ac-msg-row user">
        <div className="ac-msg user">
          <div className="ac-msg-content">
            <DiMarkdown>{message.content}</DiMarkdown>
          </div>
        </div>
      </div>
    );
  }

  const showAvatar = prevRole !== 'assistant';

  return (
    <div className="ac-msg-row assistant">
      <div className="ac-msg-avatar-slot">
        {showAvatar ? (
          assistantAvatar ? (
            <img className="ac-avatar" src={assistantAvatar} alt={assistantName} />
          ) : (
            <span
              className="ac-avatar-monogram"
              style={assistantAccent ? { background: assistantAccent } : undefined}
              aria-hidden
            >
              {monogram(assistantName)}
            </span>
          )
        ) : null}
      </div>
      <div className="ac-msg assistant">
        <div className="ac-msg-content">
          <DiMarkdown>{message.content}</DiMarkdown>
        </div>
        <div className="ac-msg-actions">
          <Tooltip title="Copy">
            <IconButton
              className="ac-icon-btn"
              size="small"
              onClick={() => onCopy(message.content)}
              aria-label="Copy"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton
              className="ac-icon-btn"
              size="small"
              onClick={() => onDownload(message.content)}
              aria-label="Download"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {message.pair_id != null && <AiSocialShareModal message={message} />}
          {message.pair_id != null && <SaveInsightsModal message={message} />}
          {message.pair_id != null && (
            <Tooltip title="Delete">
              <IconButton
                className="ac-icon-btn"
                size="small"
                onClick={() => onDelete(message.pair_id)}
                aria-label="Delete"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
});

type QueuedMessageRowProps = {
  message: QueuedMessage;
  position: number;
  onRemove: (id: number) => void;
};

const QueuedMessageRow = React.memo(function QueuedMessageRow({
  message,
  position,
  onRemove,
}: QueuedMessageRowProps) {
  return (
    <div className="ac-msg-row user ac-queued-msg-row">
      <div className="ac-msg user ac-msg-queued">
        <div className="ac-msg-content">
          <DiMarkdown>{message.content}</DiMarkdown>
        </div>
        <div className="ac-queued-msg-meta">
          <span className="ac-queued-msg-position">
            <PendingActionsIcon fontSize="inherit" aria-hidden />
            Queued · {position}
          </span>
          <Tooltip title="Remove from queue">
            <button
              type="button"
              className="ac-queued-msg-remove"
              onClick={() => onRemove(message.id)}
              aria-label={`Remove queued prompt ${position}`}
            >
              <CloseIcon fontSize="inherit" aria-hidden />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

type ThreadCardProps = {
  thread: Thread;
  onOpen: (url: string) => void;
  onDelete?: (id: any, e: React.MouseEvent) => void;
};

const ThreadCard = React.memo(function ThreadCard({ thread, onOpen, onDelete }: ThreadCardProps) {
  const canDelete = !!onDelete && thread.message_type === 'universal_chat';

  return (
    <div
      className="ac-thread"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(thread.url)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen(thread.url);
      }}
    >
      <div className="ac-thread-header">{thread.header || 'Untitled'}</div>
      <div className="ac-thread-meta">
        <span
          className="ac-thread-agent-chip"
          style={{ ['--chip-color' as any]: chipColorFor(thread.agent) }}
        >
          {thread.agent}
        </span>
        <span>{thread.date}</span>
      </div>
      {canDelete && (
        <span className="ac-thread-delete">
          <Tooltip title="Delete">
            <IconButton
              className="ac-icon-btn"
              size="small"
              aria-label="Delete thread"
              onClick={(e) => {
                e.stopPropagation();
                onDelete!(thread.id, e);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </span>
      )}
    </div>
  );
});

type ThinkingProgressProps = {
  assistantName: string;
  assistantAccent?: string;
  activity: NonNullable<AssistantChatConfig['waitingActivity']>;
  events?: ChatEvent[];
};

type LiveActivityHeartbeatProps = {
  event: ChatEvent;
  config: NonNullable<NonNullable<AssistantChatConfig['waitingActivity']>['liveEvents']>;
  longWaitAfterMs: number;
};

const LiveActivityHeartbeat = React.memo(function LiveActivityHeartbeat({
  event,
  config,
  longWaitAfterMs,
}: LiveActivityHeartbeatProps) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [receiptTime] = useState(() => event.received_at_ms || Date.now());
  const eventTime = occurredAtMs(event) ?? receiptTime;

  useEffect(() => {
    const updateElapsed = () => setElapsedMs(Math.max(0, Date.now() - eventTime));
    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(intervalId);
  }, [eventTime]);

  const isLongWait = elapsedMs >= longWaitAfterMs;
  const activeLabel = isLongWait
    ? config.longWaitMessage
    : config.activeLabels[event.event_type] || config.defaultActiveLabel;

  return (
    <li className="ac-thinking-step active live-heartbeat">
      <span className="ac-thinking-step-marker" aria-hidden="true">
        <span className="ac-thinking-step-dot" />
      </span>
      <span>
        {activeLabel}
        <span className="ac-thinking-step-elapsed" aria-hidden="true">
          {' '}· {Math.floor(elapsedMs / 1000)}s
        </span>
      </span>
    </li>
  );
});

const ThinkingProgress = React.memo(function ThinkingProgress({
  assistantName,
  assistantAccent,
  activity,
  events = [],
}: ThinkingProgressProps) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [eventAnnouncement, setEventAnnouncement] = useState('');
  const announcedEventIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const startedAt = Date.now();
    const updateElapsed = () => setElapsedMs(Date.now() - startedAt);
    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(intervalId);
  }, [activity]);

  let activeStepIndex = 0;
  activity.steps.forEach((step, index) => {
    if (elapsedMs >= step.atMs) activeStepIndex = index;
  });

  const elapsedSeconds = Math.floor(elapsedMs / 1000);
  const isLongWait = elapsedMs >= activity.longWaitAfterMs;
  const activeStatus = isLongWait
    ? activity.longWaitMessage
    : activity.steps[activeStepIndex]?.label || `${assistantName} is thinking`;
  const liveEventsConfig = activity.liveEvents;
  const recentEvents = liveEventsConfig
    ? events.slice(-Math.max(1, liveEventsConfig.recentEventLimit))
    : [];
  const hasLiveEvents = recentEvents.length > 0;
  const latestEvent = recentEvents[recentEvents.length - 1];

  useEffect(() => {
    const newlyArrived = events.filter(
      (event) => !announcedEventIdsRef.current.has(event.event_unique_id)
    );
    if (newlyArrived.length === 0) return;

    newlyArrived.forEach((event) => {
      announcedEventIdsRef.current.add(event.event_unique_id);
    });
    setEventAnnouncement(
      newlyArrived.map((event) => `${assistantName}: ${event.content}`).join('. ')
    );
  }, [assistantName, events]);

  return (
    <div
      className="ac-thinking-progress"
      style={{
        ['--ac-thinking-accent' as any]: assistantAccent || 'var(--ac-accent)',
      }}
    >
      {!hasLiveEvents && (
        <span className="ac-visually-hidden" role="status" aria-live="polite">
          {assistantName}: {activeStatus}
        </span>
      )}
      {hasLiveEvents && (
        <span
          className="ac-visually-hidden"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {eventAnnouncement}
        </span>
      )}
      <div className="ac-thinking-header">
        <div className="ac-thinking-title">
          <span className="ac-thinking-status-dot" aria-hidden="true" />
          <span>{assistantName} is thinking</span>
        </div>
        <span className="ac-thinking-elapsed" aria-hidden="true">
          {elapsedSeconds}s
        </span>
      </div>
      <ol className="ac-thinking-steps">
        {hasLiveEvents ? (
          <>
            {recentEvents.map((event, index) => {
              const isNewest = index === recentEvents.length - 1;
              return (
                <li
                  key={event.event_unique_id}
                  className={`ac-thinking-step real-event event-${event.event_type} ${isNewest ? 'current' : 'completed'}`}
                >
                  <span className="ac-thinking-step-marker" aria-hidden="true">
                    {isNewest ? eventIcon(event.event_type) : <CheckCircleIcon />}
                  </span>
                  <span>{event.content}</span>
                </li>
              );
            })}
            {latestEvent && liveEventsConfig && (
              <LiveActivityHeartbeat
                key={latestEvent.event_unique_id}
                event={latestEvent}
                config={liveEventsConfig}
                longWaitAfterMs={activity.longWaitAfterMs}
              />
            )}
          </>
        ) : (
          activity.steps.map((step, index) => {
            const state = index < activeStepIndex
              ? 'completed'
              : index === activeStepIndex
                ? 'active'
                : 'pending';
            return (
              <li key={`${step.atMs}-${step.label}`} className={`ac-thinking-step ${state}`}>
                <span className="ac-thinking-step-marker" aria-hidden="true">
                  {state === 'completed' ? (
                    <CheckCircleIcon />
                  ) : (
                    <span className="ac-thinking-step-dot" />
                  )}
                </span>
                <span>{step.label}</span>
              </li>
            );
          })
        )}
      </ol>
      {!hasLiveEvents && isLongWait && (
        <div className="ac-thinking-long-wait">{activity.longWaitMessage}</div>
      )}
    </div>
  );
});

/* ==========================================================================
   Notice cards (login / quota / error / timeout / throttled)
   ========================================================================== */

type NoticeProps = {
  variant: 'info' | 'warn' | 'danger';
  icon?: React.ReactNode;
  title?: string;
  message: React.ReactNode;
  primaryAction?: { label: React.ReactNode; href?: string; onClick?: () => void };
  secondaryAction?: { label: React.ReactNode; href?: string; onClick?: () => void };
};

function Notice({ variant, icon, title, message, primaryAction, secondaryAction }: NoticeProps) {
  return (
    <div className={`ac-notice ${variant}`}>
      {icon && <div className="ac-notice-icon">{icon}</div>}
      {title && <h4 className="ac-notice-title">{title}</h4>}
      <p className="ac-notice-message">{message}</p>
      {(primaryAction || secondaryAction) && (
        <div className="ac-notice-actions">
          {primaryAction &&
            (primaryAction.href ? (
              <a className="ac-btn-primary" href={primaryAction.href}>
                {primaryAction.label}
              </a>
            ) : (
              <button className="ac-btn-primary" type="button" onClick={primaryAction.onClick}>
                {primaryAction.label}
              </button>
            ))}
          {secondaryAction &&
            (secondaryAction.href ? (
              <a className="ac-link-muted" href={secondaryAction.href}>
                {secondaryAction.label}
              </a>
            ) : (
              <button className="ac-link-muted" type="button" onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   Main AssistantChat component
   ========================================================================== */

type AssistantChatProps = {
  config: AssistantChatConfig;
};

function AssistantChat({ config }: AssistantChatProps) {
  // Inject the stylesheet on first render (before paint) so the chat is
  // styled from the very first frame; idempotent across mounts.
  ensureStylesInjected();

  // Opt the host page into "fullscreen chat" layout. The injected CSS
  // (see assistantChatStyles.ts) keys off `body.assistant-chat-active` to
  // collapse the standard layout's content-wrapper padding and hide the
  // page footer, letting the chat fill the entire viewport below the
  // fixed navbar. Cleaned up on unmount so navigating away (via SPA-style
  // transitions) restores the normal layout.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.add('assistant-chat-active');
    return () => {
      document.body.classList.remove('assistant-chat-active');
    };
  }, []);

  // Hide global navbar + app sidebar for maximum chat space (see injected CSS
  // `body.assistant-chat-focus-mode`). Toggle restores the Rails layout chrome.
  const [appShellVisible, setAppShellVisible] = useState(false);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!appShellVisible) {
      document.body.classList.add('assistant-chat-focus-mode');
    } else {
      document.body.classList.remove('assistant-chat-focus-mode');
    }
    return () => {
      document.body.classList.remove('assistant-chat-focus-mode');
    };
  }, [appShellVisible]);

  // react-rails mounts the app inside `div[data-react-class]`, a sibling of
  // BackToTopButton under `.content-wrapper`. That mount div must participate
  // in the flex column with `flex: 1 1 0` + `min-height: 0` or it expands to
  // the chat's intrinsic height and the composer is clipped. Apply inline
  // styles here so layout is correct even if injected CSS loses a specificity
  // race with `style.css`, or if a ThemeProvider wrapper changes the DOM.
  const assistantChatPageRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const pageEl = assistantChatPageRef.current;
    if (!pageEl) return undefined;
    const mountEl = pageEl.parentElement?.closest('[data-react-class]');
    if (!mountEl || !(mountEl instanceof HTMLElement)) return undefined;
    if (mountEl.getAttribute('data-react-class') === 'BackToTopButton') {
      return undefined;
    }

    const h = mountEl;
    const prev = {
      flex: h.style.flex,
      flexGrow: h.style.flexGrow,
      flexShrink: h.style.flexShrink,
      flexBasis: h.style.flexBasis,
      minHeight: h.style.minHeight,
      display: h.style.display,
      flexDirection: h.style.flexDirection,
      overflow: h.style.overflow,
    };

    h.style.flex = '1 1 0';
    h.style.minHeight = '0';
    h.style.display = 'flex';
    h.style.flexDirection = 'column';
    h.style.overflow = 'hidden';

    return () => {
      h.style.flex = prev.flex;
      h.style.flexGrow = prev.flexGrow;
      h.style.flexShrink = prev.flexShrink;
      h.style.flexBasis = prev.flexBasis;
      h.style.minHeight = prev.minHeight;
      h.style.display = prev.display;
      h.style.flexDirection = prev.flexDirection;
      h.style.overflow = prev.overflow;
    };
  }, []);

  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('md'));

  // ---------- State ----------
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [threads, setThreads] = useState<Thread[]>(config.initial?.threads || []);
  const [threadSearchQuery, setThreadSearchQuery] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    try {
      return window.localStorage.getItem(THREAD_SEARCH_STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [threadsVisibleCap, setThreadsVisibleCap] = useState<number>(() =>
    Math.min(THREADS_VISIBLE_BATCH, (config.initial?.threads || []).length)
  );

  const [discussionId, setDiscussionId] = useState<any>(config.initial?.discussionId);
  const [discussionTitle, setDiscussionTitle] = useState<string | undefined>(
    config.initial?.discussionTitle
  );

  const [fetchingAnswer, setFetchingAnswer] = useState(false);
  const [sendable, setSendable] = useState(false);
  const [queuedMessages, setQueuedMessages] = useState<QueuedMessage[]>([]);
  const [queuePauseReason, setQueuePauseReason] = useState<QueuePauseReason>(null);

  const [loginRequired, setLoginRequired] = useState(() => config.initial?.signedIn === false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [requestTimeout, setRequestTimeout] = useState(false);
  const [throttled, setThrottled] = useState(false);

  const [chatEvents, setChatEvents] = useState<ChatEvent[]>([]);

  const [renameOpen, setRenameOpen] = useState(false);
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [deleteMessageOpen, setDeleteMessageOpen] = useState(false);
  const [deleteThreadOpen, setDeleteThreadOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<any>(null);
  const [threadToDelete, setThreadToDelete] = useState<any>(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [composerFocused, setComposerFocused] = useState(false);
  const [composerValue, setComposerValue] = useState('');

  // ---------- Refs ----------
  const newMessageRef = useRef<HTMLTextAreaElement | null>(null);
  const newTitleRef = useRef<HTMLInputElement | null>(null);
  const conversationRef = useRef<HTMLDivElement | null>(null);

  // After /chat loads (e.g. post-login redirect), mobile browsers may restore a
  // non-zero window scroll; resetting keeps the flex stack aligned so the
  // docked composer is visible (with 100dvh in assistantChatStyles for Mobile Safari).
  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
      if (document.documentElement) document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    const conv = conversationRef.current;
    if (conv) conv.scrollTop = 0;
  }, []);

  const pollTimeoutRef = useRef<number | null>(null);
  const pollStartRef = useRef<number>(0);
  const pollAttemptRef = useRef<number>(0);
  const eventsPollTimeoutRef = useRef<number | null>(null);
  const activeAsyncIdRef = useRef<string | null>(null);
  const fetchingAnswerRef = useRef<boolean>(false);
  const queuedMessagesRef = useRef<QueuedMessage[]>([]);
  const queuePauseReasonRef = useRef<QueuePauseReason>(null);
  const nextQueuedMessageIdRef = useRef(1);
  const discussionIdRef = useRef<any>(discussionId);
  const initialFetchAbortRef = useRef<AbortController | null>(null);
  const skipNextPreviousMessagesFetchRef = useRef(false);
  // Single component-lifetime AbortController used for all in-flight send/poll/events
  // fetches so they're cancelled cleanly on unmount (no setState-on-unmounted warnings).
  const liveAbortRef = useRef<AbortController>(new AbortController());
  const mountedRef = useRef(true);
  /** After `messages` updates, scroll `.ac-conversation` to bottom (set before `setMessages`). */
  const pendingConversationScrollRef = useRef<'smooth' | 'auto' | null>(null);
  const prevThreadSearchRef = useRef<string | null>(null);

  const sendCfg = config.api.send;
  const isAsync = sendCfg.mode === 'async';

  const pollIntervals =
    isAsync ? (sendCfg as AsyncSend).pollIntervals || DEFAULT_POLL_INTERVALS : DEFAULT_POLL_INTERVALS;
  const maxPollDuration =
    isAsync ? (sendCfg as AsyncSend).maxPollDuration || DEFAULT_MAX_POLL_DURATION : DEFAULT_MAX_POLL_DURATION;
  const eventsPollInterval =
    isAsync
      ? (sendCfg as AsyncSend).eventsPollInterval || DEFAULT_EVENTS_POLL_INTERVAL
      : DEFAULT_EVENTS_POLL_INTERVAL;

  const isUniversalChat =
    isAsync && (sendCfg as AsyncSend).bodyExtras?.message_type === 'universal_chat';

  const resolvedPreviousMessagesUrl = useMemo(() => {
    if (!isUniversalChat) return config.api.previousMessagesUrl;
    if (discussionId == null || discussionId === '') return null;
    const raw = config.api.previousMessagesUrl;
    const qIdx = raw.indexOf('?');
    const q = qIdx >= 0 ? raw.slice(qIdx) : '';
    return `/chat/${discussionId}/previous_messages${q}`;
  }, [isUniversalChat, discussionId, config.api.previousMessagesUrl]);

  // ---------- Mount/unmount cleanup ----------
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPollingTimeout();
      clearEventsPolling();
      if (initialFetchAbortRef.current) initialFetchAbortRef.current.abort();
      liveAbortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchingAnswerRef.current = fetchingAnswer;
    if (!fetchingAnswer) clearEventsPolling();
  }, [fetchingAnswer]);

  useEffect(() => {
    discussionIdRef.current = discussionId;
  }, [discussionId]);

  // Scroll the message pane after React commits new DOM — `scrollIntoView` on a
  // nested span often ran before `appendAssistantAnswer` painted the assistant
  // message, so the thread never scrolled to the true bottom.
  useLayoutEffect(() => {
    const el = conversationRef.current;
    if (!el) return;
    const mode = pendingConversationScrollRef.current;
    if (mode === null) return;
    pendingConversationScrollRef.current = null;
    el.scrollTo({ top: el.scrollHeight, behavior: mode });
  }, [messages, queuedMessages]);

  // Keep the tail in view while the typing / events UI changes height mid-flight.
  useLayoutEffect(() => {
    if (!fetchingAnswer) return;
    const el = conversationRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [fetchingAnswer]);

  // ---------- Fetch previous messages on mount and when discussionId changes ----------
  useEffect(() => {
    if (resolvedPreviousMessagesUrl == null) return undefined;
    if (skipNextPreviousMessagesFetchRef.current) {
      skipNextPreviousMessagesFetchRef.current = false;
      return undefined;
    }

    // Cancel any in-flight initial fetch before starting a new one.
    if (initialFetchAbortRef.current) initialFetchAbortRef.current.abort();
    const controller = new AbortController();
    initialFetchAbortRef.current = controller;

    fetch(resolvedPreviousMessagesUrl, { signal: controller.signal })
      .then((response) => {
        if (response.status === 401) {
          if (mountedRef.current) setLoginRequired(true);
          return [];
        } else if (response.status === 200) {
          if (mountedRef.current) setLoginRequired(false);
        }
        return response.json();
      })
      .then((data) => {
        if (!mountedRef.current) return;
        const list = Array.isArray(data) ? data : [];
        if (list.length > 0) {
          pendingConversationScrollRef.current = 'auto';
        }
        setMessages(list);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.log(error?.message);
      });

    return () => {
      controller.abort();
      clearPollingTimeout();
      clearEventsPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPreviousMessagesUrl]);

  // ---------- Thread list: search filter + visible cap reset / clamp ----------
  const filteredThreads = useMemo(() => {
    const q = threadSearchQuery.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => {
      const hay = `${t.header ?? ''} ${t.agent ?? ''} ${t.date ?? ''} ${t.url ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [threads, threadSearchQuery]);

  useEffect(() => {
    const max = filteredThreads.length;
    if (prevThreadSearchRef.current !== threadSearchQuery) {
      prevThreadSearchRef.current = threadSearchQuery;
      setThreadsVisibleCap(Math.min(THREADS_VISIBLE_BATCH, max));
    } else {
      setThreadsVisibleCap((c) => Math.min(c, max));
    }
  }, [filteredThreads, threadSearchQuery]);

  // Persist the sidebar search query across reloads (shared across all assistants).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (threadSearchQuery) {
        window.localStorage.setItem(THREAD_SEARCH_STORAGE_KEY, threadSearchQuery);
      } else {
        window.localStorage.removeItem(THREAD_SEARCH_STORAGE_KEY);
      }
    } catch { /* ignore quota / private mode */ }
  }, [threadSearchQuery]);

  // ---------- Polling helpers ----------
  function clearPollingTimeout() {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }

  function clearEventsPolling() {
    if (eventsPollTimeoutRef.current) {
      clearTimeout(eventsPollTimeoutRef.current);
      eventsPollTimeoutRef.current = null;
    }
  }

  function resetEventTracking() {
    clearEventsPolling();
    setChatEvents([]);
  }

  function finalizeEventTracking() {
    clearEventsPolling();
  }

  function scheduleEventsPolling() {
    if (!config.features?.activityEvents) return;
    const asyncId = activeAsyncIdRef.current;
    if (!asyncId) return;

    const fetchAndSchedule = () => {
      const currentAsyncId = activeAsyncIdRef.current;
      if (!currentAsyncId) return;

      fetch(`/async_chat_messages/${currentAsyncId}/events`, {
        signal: liveAbortRef.current.signal,
      })
        .then((response) => {
          if (response.status === 401) {
            setLoginRequired(true);
            return null;
          } else if (response.status === 403) {
            setQuotaExceeded(true);
            return null;
          } else if (response.status === 404) {
            return null;
          } else if (response.status === 200) {
            setLoginRequired(false);
            return response.json();
          }
          return null;
        })
        .then((data) => {
          if (!data || !Array.isArray(data)) return;
          if (activeAsyncIdRef.current !== currentAsyncId) return;
          setChatEvents((prev) => {
            return mergeChatEvents(prev, data as ChatEvent[]);
          });
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.log(error?.message);
        })
        .finally(() => {
          if (
            mountedRef.current &&
            activeAsyncIdRef.current === currentAsyncId
          ) {
            eventsPollTimeoutRef.current = window.setTimeout(
              fetchAndSchedule,
              eventsPollInterval
            );
          }
        });
    };

    fetchAndSchedule();
  }

  // ---------- Send / receive ----------
  function updateQueue(next: QueuedMessage[]) {
    queuedMessagesRef.current = next;
    setQueuedMessages(next);
  }

  function updateQueuePauseReason(reason: QueuePauseReason) {
    queuePauseReasonRef.current = reason;
    setQueuePauseReason(reason);
  }

  function enqueueMessage(text: string) {
    const next = [
      ...queuedMessagesRef.current,
      { id: nextQueuedMessageIdRef.current++, content: text },
    ];
    pendingConversationScrollRef.current = 'smooth';
    updateQueue(next);
  }

  function removeQueuedMessage(id: number) {
    const next = queuedMessagesRef.current.filter((message) => message.id !== id);
    updateQueue(next);
    if (next.length === 0) updateQueuePauseReason(null);
  }

  function resumeQueue() {
    if (
      queuePauseReasonRef.current !== 'retryable' ||
      fetchingAnswerRef.current ||
      queuedMessagesRef.current.length === 0
    ) {
      return;
    }
    updateQueuePauseReason(null);
  }

  function finishActiveRequest(outcome: 'success' | 'retryable' | 'blocked') {
    clearPollingTimeout();
    clearEventsPolling();
    activeAsyncIdRef.current = null;

    if (outcome === 'success' || queuedMessagesRef.current.length === 0) {
      updateQueuePauseReason(null);
    } else {
      updateQueuePauseReason(outcome === 'blocked' ? 'blocked' : 'retryable');
    }

    fetchingAnswerRef.current = false;
    setFetchingAnswer(false);
  }

  function startRequest(text: string) {
    if (!text || text.trim() === '') return;
    setThrottled(false);
    if (config.features?.activityEvents) resetEventTracking();
    setRequestTimeout(false);
    setServerError(false);
    setQuotaExceeded(false);
    updateQueuePauseReason(null);
    fetchingAnswerRef.current = true;
    setFetchingAnswer(true);
    pendingConversationScrollRef.current = 'smooth';
    setMessages((prev) => [...prev, { role: 'user', content: text }]);

    if (sendCfg.mode === 'sync') {
      sendSync(text, sendCfg as SyncSend);
    } else {
      sendAsync(text, sendCfg as AsyncSend);
    }
  }

  function submitMessage(text: string) {
    if (!text || text.trim() === '') return;

    setComposerValue('');
    setSendable(false);

    const mustQueue =
      fetchingAnswerRef.current ||
      queuedMessagesRef.current.length > 0 ||
      queuePauseReasonRef.current !== null;

    if (mustQueue) {
      enqueueMessage(text);
    } else {
      startRequest(text);
    }

    requestAnimationFrame(() => newMessageRef.current?.focus());
  }

  function sendSync(question: string, cfg: SyncSend) {
    fetch(cfg.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, ...(cfg.bodyExtras || {}) }),
      signal: liveAbortRef.current.signal,
    })
      .then((response) => {
        if (response.status === 401) {
          setLoginRequired(true);
          finishActiveRequest('blocked');
          return null;
        } else if (response.status === 403) {
          setQuotaExceeded(true);
          finishActiveRequest('blocked');
          return null;
        } else if (response.status === 429) {
          setThrottled(true);
          finishActiveRequest('retryable');
          return null;
        } else if (response.status === 504) {
          setRequestTimeout(true);
          finishActiveRequest('retryable');
          return null;
        } else if (response.status === 500) {
          setServerError(true);
          finishActiveRequest('retryable');
          return null;
        } else if (response.status === 200) {
          setLoginRequired(false);
          return response.json();
        }
        setServerError(true);
        finishActiveRequest('retryable');
        return null;
      })
      .then((data) => {
        if (!data) {
          if (fetchingAnswerRef.current) {
            setServerError(true);
            finishActiveRequest('retryable');
          }
          return;
        }
        appendAssistantAnswer(question, data);
        finishActiveRequest('success');
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.log(error?.message);
        setServerError(true);
        finishActiveRequest('retryable');
      });
  }

  function sendAsync(question: string, cfg: AsyncSend) {
    clearPollingTimeout();
    activeAsyncIdRef.current = null;

    fetch(cfg.postUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question,
        discussion_id:
          discussionIdRef.current == null ? null : discussionIdRef.current,
        ...cfg.bodyExtras,
      }),
      signal: liveAbortRef.current.signal,
    })
      .then((response) => {
        if (response.status === 401) {
          setLoginRequired(true);
          finishActiveRequest('blocked');
          return null;
        } else if (response.status === 403) {
          setQuotaExceeded(true);
          finishActiveRequest('blocked');
          return null;
        } else if (response.status === 504) {
          setRequestTimeout(true);
          finishActiveRequest('retryable');
          return null;
        } else if (response.status === 429) {
          setThrottled(true);
          finishActiveRequest('retryable');
          return null;
        } else if (response.status === 500) {
          setServerError(true);
          finishActiveRequest('retryable');
          return null;
        } else if (response.status === 200) {
          setLoginRequired(false);
          return response.json();
        }
        setServerError(true);
        finishActiveRequest('retryable');
        return null;
      })
      .then((data) => {
        if (!data || !data.async_id) {
          if (fetchingAnswerRef.current) {
            setServerError(true);
            finishActiveRequest('retryable');
          }
          return;
        }
        activeAsyncIdRef.current = data.async_id;
        pollStartRef.current = Date.now();
        pollAttemptRef.current = 0;
        if (config.features?.activityEvents) {
          resetEventTracking();
          scheduleEventsPolling();
        }
        scheduleAnswerPolling(question);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.log(error?.message);
        setServerError(true);
        finishActiveRequest('retryable');
      });
  }

  function handleAsyncTimeout() {
    clearPollingTimeout();
    clearEventsPolling();
    setRequestTimeout(true);
    finishActiveRequest('retryable');
  }

  function scheduleAnswerPolling(question: string) {
    const asyncId = activeAsyncIdRef.current;
    if (!asyncId) return handleAsyncTimeout();
    const elapsed = Date.now() - pollStartRef.current;
    if (elapsed >= maxPollDuration) return handleAsyncTimeout();

    const delay = pollIntervals[Math.min(pollAttemptRef.current, pollIntervals.length - 1)]!;
    pollAttemptRef.current += 1;

    pollTimeoutRef.current = window.setTimeout(() => {
      fetch(`/async_chat_messages/${asyncId}`, {
        signal: liveAbortRef.current.signal,
      })
        .then((response) => {
          if (response.status === 202) {
            scheduleAnswerPolling(question);
            return null;
          } else if (response.status === 401) {
            setLoginRequired(true);
          } else if (response.status === 403) {
            setQuotaExceeded(true);
          } else if (response.status === 429) {
            setThrottled(true);
          } else if (response.status === 504) {
            setRequestTimeout(true);
          } else if (response.status === 500) {
            setServerError(true);
          } else if (response.status === 200) {
            setLoginRequired(false);
            return response.json();
          } else {
            setServerError(true);
          }

          if (response.status !== 202) {
            const blocked = response.status === 401 || response.status === 403;
            finishActiveRequest(blocked ? 'blocked' : 'retryable');
          }
          return null;
        })
        .then((data) => {
          if (!data) return;
          clearPollingTimeout();
          handleAsyncResponse(question, data);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          console.log(error?.message);
          handleAsyncTimeout();
        });
    }, delay);
  }

  function handleAsyncResponse(question: string, data: any) {
    if (data.status && data.status !== 'completed') {
      console.error(data.error || 'Async response failed');
      setServerError(true);
      finishActiveRequest('retryable');
      return;
    }
    if (
      data.async_id &&
      activeAsyncIdRef.current &&
      data.async_id !== activeAsyncIdRef.current
    ) {
      return;
    }
    appendAssistantAnswer(question, data);

    if (config.features?.discussionUrlPush && data.discussion_id) {
      const changed =
        !discussionIdRef.current || discussionIdRef.current !== data.discussion_id;
      if (changed) {
        try {
          window.history.pushState(
            'AssistantChat',
            config.discussionPageTitle || document.title,
            `/chat/${data.discussion_id}`
          );
        } catch {
          /* noop */
        }
        skipNextPreviousMessagesFetchRef.current = true;
        discussionIdRef.current = data.discussion_id;
        setDiscussionId(data.discussion_id);
      }
    }
    if (data.title && !discussionTitle) {
      setDiscussionTitle(data.title);
    }
    if (config.features?.activityEvents) finalizeEventTracking();
    finishActiveRequest('success');
  }

  function appendAssistantAnswer(_question: string, data: any) {
    pendingConversationScrollRef.current = 'smooth';
    setMessages((prev) => {
      const updated = [...prev];
      // Back-stitch pair_id onto the most-recent user message that lacks one.
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i]!.role === 'user' && !updated[i]!.pair_id) {
          updated[i] = { ...updated[i]!, pair_id: data.pair_id };
          break;
        }
      }
      return [
        ...updated,
        {
          role: 'assistant',
          content: data.answer,
          pair_id: data.pair_id,
          title: data.title,
          public_url: data.public_url,
          unique_id: data.unique_id,
        },
      ];
    });
  }

  // Start exactly one queued request after the active request has committed its
  // terminal UI/state. The immediate refs close the small gap between this
  // effect scheduling the work and React committing fetchingAnswer=true.
  useEffect(() => {
    if (
      fetchingAnswer ||
      fetchingAnswerRef.current ||
      queuePauseReasonRef.current !== null ||
      queuedMessagesRef.current.length === 0
    ) {
      return;
    }

    const [nextMessage, ...remaining] = queuedMessagesRef.current;
    if (!nextMessage) return;
    updateQueue(remaining);
    startRequest(nextMessage.content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingAnswer, queuePauseReason, queuedMessages]);

  // ---------- Stable message handlers ----------
  const handleCopy = useCallback((content: string) => {
    navigator.clipboard.writeText(content);
  }, []);

  const handleDownload = useCallback(
    (content: string) => {
      const element = document.createElement('a');
      const file = new Blob([content], { type: 'text/plain' });
      element.href = URL.createObjectURL(file);
      element.download = config.downloadFilenameBuilder(content);
      document.body.appendChild(element);
      element.click();
      element.remove();
      setTimeout(() => URL.revokeObjectURL(element.href), 0);
    },
    [config]
  );

  const handleDeleteMessage = useCallback((pairId: any) => {
    setMessageToDelete(pairId);
    setDeleteMessageOpen(true);
  }, []);

  function confirmDeleteMessage() {
    const id = messageToDelete;
    setMessages((list) => list.filter((m) => m.pair_id !== id));
    fetch(`/gpt_company_messages/${id}`, { method: 'DELETE' })
      .then((r) => r.json)
      .catch(() => undefined);
    setDeleteMessageOpen(false);
  }

  const openThread = useCallback((url: string) => {
    window.location.replace(url);
  }, []);

  const handleDeleteThread = useCallback((id: any) => {
    setThreadToDelete(id);
    setDeleteThreadOpen(true);
  }, []);

  function confirmDeleteThread() {
    const id = threadToDelete;
    fetch(`/chat_discussion_threads/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((response) => {
        if (response.ok) {
          setThreads((prev) => prev.filter((t) => t.id !== id));
        }
      })
      .catch((error) => console.error('Error:', error));
    setDeleteThreadOpen(false);
  }

  function submitRename(e: React.SyntheticEvent) {
    e.preventDefault();
    const newTitle = newTitleRef.current?.value || '';
    if (newTitle.trim() === '') return;
    fetch(`/chat_discussion_threads/${discussionId == null ? null : discussionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: newTitle }),
    })
      .then((response) => {
        if (response.status === 200) setDiscussionTitle(newTitle);
        return response.json();
      })
      .catch(() => undefined);
    setRenameOpen(false);
  }

  // ---------- Composer events ----------
  function onComposerChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const v = e.target.value;
    setComposerValue(v);
    setSendable(v.trim().length > 0);
  }

  function onComposerKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitMessage(composerValue);
    } else if (e.key === 'Escape') {
      (e.target as HTMLTextAreaElement).blur();
    }
  }

  function onPickExample(prompt: string) {
    setComposerValue(prompt);
    setSendable(prompt.trim().length > 0);
    requestAnimationFrame(() => {
      newMessageRef.current?.focus();
    });
  }

  // ---------- Derived ----------
  const isEmpty = messages.length === 0;
  const allPrompts = useMemo(
    () => config.examples.flatMap((g) => g.prompts),
    [config.examples]
  );
  const placeholder = useRotatingPlaceholder(
    allPrompts,
    composerFocused || composerValue.length > 0 || allPrompts.length === 0,
    'Ask a question'
  );
  const composerWillQueue =
    fetchingAnswer || queuedMessages.length > 0 || queuePauseReason !== null;

  const visibleThreads = useMemo(
    () => filteredThreads.slice(0, threadsVisibleCap),
    [filteredThreads, threadsVisibleCap]
  );

  const signInHref = useMemo(() => {
    if (!loginRequired || typeof window === 'undefined') return '/signin';
    const path = `${window.location.pathname}${window.location.search}`;
    return `/signin?return_to=${encodeURIComponent(path)}`;
  }, [loginRequired]);

  const visibleNotice = useMemo(() => {
    if (loginRequired) {
      return (
        <Notice
          variant="info"
          icon={<i className="mdi mdi-login" />}
          title="Sign in required"
          message="Please sign in to continue using the AI assistant."
          primaryAction={{ label: 'Sign In', href: signInHref }}
        />
      );
    }
    if (quotaExceeded) {
      return (
        <Notice
          variant="warn"
          icon={<i className="mdi mdi-alert-circle-outline" />}
          title="Usage limit reached"
          message={
            <>
              You've reached your limit of questions/tasks with{' '}
              <strong>{config.assistant.name}</strong>. Upgrade to continue getting valuable
              market insights.
            </>
          }
          primaryAction={{
            label: (
              <>
                <i className="mdi mdi-rocket-launch-outline" /> Upgrade to continue
              </>
            ),
            href: '/pricing',
          }}
          secondaryAction={{ label: 'View my usage details', href: '/dashboard' }}
        />
      );
    }
    if (serverError) {
      return (
        <Notice
          variant="danger"
          icon={<i className="mdi mdi-alert-octagon-outline" />}
          title="Something went wrong"
          message={
            <>
              Sorry, something went wrong. Please refresh the page or{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); location.reload(); }}>
                try again
              </a>
              .
            </>
          }
        />
      );
    }
    if (requestTimeout) {
      return (
        <Notice
          variant="info"
          icon={<i className="mdi mdi-timer-sand" />}
          title="This is taking a while"
          message={
            <>
              Click{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); location.reload(); }}>
                here
              </a>{' '}
              to see the results or refresh the page.
            </>
          }
        />
      );
    }
    if (throttled) {
      return (
        <Notice
          variant="warn"
          icon={<i className="mdi mdi-traffic-light" />}
          title="High traffic"
          message="Sorry, we're experiencing high traffic. Please try again in 2 minutes."
        />
      );
    }
    return null;
  }, [loginRequired, quotaExceeded, serverError, requestTimeout, throttled, config.assistant.name, signInHref]);

  // ---------- Render helpers ----------
  function renderEmptyHero() {
    return (
      <div className="ac-empty-state">
        <div className="ac-empty-avatar">
          {config.assistant.avatar ? (
            <img src={config.assistant.avatar} alt={config.assistant.name} />
          ) : (
            <span>{monogram(config.assistant.name)}</span>
          )}
        </div>
        <h2 className="ac-empty-name">{config.assistant.name}</h2>
        <p className="ac-empty-context">{config.introContext}</p>
      </div>
    );
  }

  function renderConversation() {
    return (
      <div className="ac-conversation-inner">
        {messages.map((m, i) => (
          <MessageRow
            key={`${m.pair_id ?? 'm'}-${i}`}
            message={m}
            prevRole={i > 0 ? messages[i - 1]!.role : null}
            assistantName={config.assistant.name}
            assistantAvatar={config.assistant.avatar}
            assistantAccent={config.assistant.accent}
            onCopy={handleCopy}
            onDownload={handleDownload}
            onDelete={handleDeleteMessage}
          />
        ))}
        {fetchingAnswer && config.waitingActivity && (
          <ThinkingProgress
            assistantName={config.assistant.name}
            assistantAccent={config.assistant.accent}
            activity={config.waitingActivity}
            events={config.features?.activityEvents ? chatEvents : undefined}
          />
        )}
        {fetchingAnswer && !config.waitingActivity && (
          <div className="ac-msg-row assistant">
            <div className="ac-msg-avatar-slot" />
            <div className="ac-msg assistant">
              <div className="ac-typing" aria-label="Assistant is typing">
                <span /><span /><span />
              </div>
            </div>
          </div>
        )}
        {visibleNotice}
        {queuedMessages.length > 0 && (
          <section
            className={`ac-queue${queuePauseReason ? ' paused' : ''}`}
            aria-label="Queued prompts"
          >
            <div className="ac-queue-status" role="status" aria-live="polite">
              <span>
                {queuePauseReason
                  ? `Queue paused · ${queuedMessages.length} waiting`
                  : `${queuedMessages.length} queued`}
              </span>
              {queuePauseReason === 'retryable' && (
                <button type="button" className="ac-queue-resume" onClick={resumeQueue}>
                  Resume queue
                </button>
              )}
            </div>
            <div className="ac-queue-messages">
              {queuedMessages.map((message, index) => (
                <QueuedMessageRow
                  key={message.id}
                  message={message}
                  position={index + 1}
                  onRemove={removeQueuedMessage}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    );
  }

  // ---------- Render ----------
  return (
    <div className="assistant-chat-page" ref={assistantChatPageRef}>
      {config.breadcrumb && (
        <div className="page-header assistant-chat-breadcrumb">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              {config.breadcrumb.map((b, i) => {
                const isLast = i === config.breadcrumb!.length - 1;
                return (
                  <li
                    key={i}
                    className={`breadcrumb-item${isLast ? ' active' : ''}`}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {!isLast && b.href ? <a href={b.href}>{b.label}</a> : b.label}
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      )}

      <div className={`assistant-chat${sidebarOpen ? ' ac-sidebar-open' : ''}`}>
        {/* Sidebar */}
        <aside className="ac-sidebar" aria-label="Chat threads">
          <div className="ac-sidebar-header">
            <div className="ac-sidebar-title-row">
              <h2 className="ac-sidebar-title">{discussionTitle || config.sidebarTitle}</h2>
              <div className="ac-sidebar-actions">
                {config.features?.newChat && (
                  <Tooltip title="New chat">
                    <a className="ac-icon-btn" href="/chat" aria-label="New chat">
                      <AddIcon />
                    </a>
                  </Tooltip>
                )}
                {config.features?.rename && (
                  <Tooltip title="Rename chat">
                    <button
                      className="ac-icon-btn"
                      type="button"
                      aria-label="Rename chat"
                      onClick={() => setRenameOpen(true)}
                    >
                      <EditIcon />
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
            {config.sidebarSubtitle && (
              <p className="ac-sidebar-subtitle">{config.sidebarSubtitle}</p>
            )}
          </div>

          {config.contextHeader && (
            <div className="ac-sidebar-context">{config.contextHeader}</div>
          )}

          {threads.length > 0 && (
            <div className="ac-thread-search-wrap">
              <TextField
                className="ac-thread-search-field"
                size="small"
                fullWidth
                placeholder="Search chats…"
                value={threadSearchQuery}
                onChange={(e) => setThreadSearchQuery(e.target.value)}
                inputProps={{ 'aria-label': 'Search chat threads' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'var(--ac-text-muted)', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: threadSearchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Clear search"
                        onClick={() => setThreadSearchQuery('')}
                        edge="end"
                        sx={{ color: 'var(--ac-text-muted)' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </div>
          )}

          <div className="ac-thread-list">
            {visibleThreads.map((t) => (
              <ThreadCard
                key={t.id}
                thread={t}
                onOpen={openThread}
                onDelete={handleDeleteThread}
              />
            ))}
            {threadSearchQuery.trim() && filteredThreads.length === 0 && threads.length > 0 && (
              <p className="ac-thread-empty-filter">No chats match your search.</p>
            )}
            {threadsVisibleCap < filteredThreads.length && (
              <button
                type="button"
                className="ac-thread-more-btn"
                onClick={() =>
                  setThreadsVisibleCap((c) =>
                    Math.min(c + THREADS_VISIBLE_BATCH, filteredThreads.length)
                  )
                }
              >
                + More
              </button>
            )}
          </div>
        </aside>

        {/* Backdrop (mobile only) */}
        <div
          className="ac-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />

        {/* Main pane */}
        <div className="ac-main">
          <div className="ac-main-header">
            <Tooltip
              title={
                appShellVisible ? 'Focus on chat (hide site navigation)' : 'Show site navigation'
              }
            >
              <button
                type="button"
                className="ac-icon-btn ac-site-shell-toggle"
                aria-label={appShellVisible ? 'Focus on chat' : 'Show site navigation'}
                aria-pressed={appShellVisible}
                onClick={() => setAppShellVisible((v) => !v)}
              >
                {appShellVisible ? <MenuOpenIcon /> : <ViewSidebarOutlinedIcon />}
              </button>
            </Tooltip>
            <button
              className="ac-icon-btn ac-burger"
              type="button"
              aria-label="Toggle threads"
              onClick={() => setSidebarOpen((v) => !v)}
            >
              {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
            <div className="ac-main-header-info">
              {config.assistant.avatar ? (
                <img className="ac-avatar" src={config.assistant.avatar} alt={config.assistant.name} />
              ) : (
                <span
                  className="ac-avatar-monogram"
                  style={config.assistant.accent ? { background: config.assistant.accent } : undefined}
                  aria-hidden
                >
                  {monogram(config.assistant.name)}
                </span>
              )}
              <div className="ac-header-text">
                <div className="ac-header-name">{config.assistant.name}</div>
                <div className="ac-header-context">{config.introContext}</div>
              </div>
            </div>
          </div>

          <div className="ac-conversation" ref={conversationRef}>
            {isEmpty && !visibleNotice ? renderEmptyHero() : renderConversation()}
          </div>

          {!loginRequired && (
            <div className="ac-composer-wrap">
              <div className="ac-composer-inner">
                <form
                  action="#"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitMessage(composerValue);
                  }}
                >
                  <div className="ac-composer">
                    <Tooltip title="See example prompts">
                      <button
                        type="button"
                        className="ac-composer-examples"
                        onClick={() => setExamplesOpen(true)}
                        aria-label="See example prompts"
                      >
                        <LightbulbOutlinedIcon fontSize="small" aria-hidden />
                      </button>
                    </Tooltip>
                    <TextareaAutosize
                      ref={newMessageRef as any}
                      className="ac-composer-textarea"
                      minRows={1}
                      maxRows={8}
                      placeholder={
                        composerWillQueue ? 'Add another question to the queue' : placeholder
                      }
                      value={composerValue}
                      onChange={onComposerChange}
                      onKeyDown={onComposerKeyDown}
                      onFocus={() => setComposerFocused(true)}
                      onBlur={() => setComposerFocused(false)}
                      aria-label="Ask a question"
                    />
                    <button
                      type="submit"
                      className="ac-composer-send"
                      disabled={!sendable}
                      aria-label={composerWillQueue ? 'Add to queue' : 'Send'}
                    >
                      <SendIcon />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rename dialog (Universal only — gated by features.rename) */}
      {config.features?.rename && (
        <Dialog
          fullScreen={fullScreenDialog}
          open={renameOpen}
          onClose={() => setRenameOpen(false)}
        >
          <DialogTitle>Rename chat</DialogTitle>
          <DialogContent>
            <DialogContentText>Enter a new title for this chat.</DialogContentText>
            <TextField
              inputRef={newTitleRef}
              required
              autoFocus
              margin="dense"
              label="New title"
              fullWidth
              variant="standard"
            />
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button size="small" onClick={submitRename}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete-message confirm */}
      <Dialog open={deleteMessageOpen} onClose={() => setDeleteMessageOpen(false)}>
        <DialogTitle>Confirm deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete this message?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMessageOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteMessage} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete-thread confirm */}
      <Dialog open={deleteThreadOpen} onClose={() => setDeleteThreadOpen(false)}>
        <DialogTitle>Confirm thread deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete this chat thread?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteThreadOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteThread} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Examples popover — only mounted when opened (Dialog itself uses a portal) */}
      {examplesOpen && (
        <AssistantChatExamplesDialog
          open={examplesOpen}
          onClose={() => setExamplesOpen(false)}
          examples={config.examples}
          initialTab={config.initialExampleTab || 0}
          onPick={onPickExample}
        />
      )}
    </div>
  );
}

export default AssistantChat;
